/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: How to use section for AI Image Generator landing page
 */
import Image from 'next/image';
import React from 'react';

const CDN_BASE = 'https://assets.falcocut.ai/ai-image-generator';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Enter Your Prompt or Upload Image',
    description:
      'Describe what you want to see or upload a photo to transform. Keep it simple — just tell FalcoCut what you want to see.',
  },
  {
    number: 2,
    title: 'Choose the AI Model',
    description:
      'Pick an AI model like Nano Banana or Flux Kontext that best matches your project.',
  },
  {
    number: 3,
    title: 'Set Aspect Ratio and Resolution',
    description:
      'Choose an aspect ratio that matches your platform, such as square (1:1), vertical (9:16, 4:5, 3:4), or landscape (16:9). Then select the output resolution—1K, 2K, or 4K—based on your quality needs.',
  },
  {
    number: 4,
    title: 'Save and Share Your Image',
    description:
      'Click Start to generate your image. Once finished, download it, reuse it, or create variations by adjusting the prompt, model, or settings anytime.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    {/* 步骤编号 */}
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    {/* 步骤内容 */}
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Make an AI Image with FalcoCut
        </h2>

        {/* 内容区域 */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* 左侧图片 */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src={`${CDN_BASE}/how-to-make-an-ai-image-with-falcocut.webp`}
                alt="FalcoCut AI Image Generator Interface"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* 右侧步骤 */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
